import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, InfoIcon, MailIcon } from 'lucide-react';
import { ConsultantAvatar } from '../components/consultants/ConsultantAvatar';
import { ButtonLink } from '../components/ui/ButtonLink';
import { PageHero } from '../components/ui/PageHero';
import { consultants, searchApproach } from '../data/consultants';
import { usePageMeta } from '../hooks/usePageMeta';
import { buttonClasses } from '../utils/buttonStyles';
import { NotFound } from './NotFound';
export function ConsultantProfile() {
  const {
    slug
  } = useParams();
  const consultant = consultants.find(c => c.slug === slug);
  usePageMeta(consultant ? `${consultant.name}, ${consultant.title}` : 'Consultant not found', consultant ? `${consultant.name} is an Executive Search Consultant with Aurevane Search Partners. Contact ${consultant.firstName} at ${consultant.email}.` : 'The consultant profile you are looking for could not be found.', consultant ? {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: consultant.name,
    jobTitle: consultant.title,
    email: consultant.email,
    worksFor: {
      '@type': 'Organization',
      name: 'Aurevane Search Partners'
    }
  } : undefined);
  if (!consultant) return <NotFound data-id="element-632" data-mp="17i3f4o1ar20r" />;
  const others = consultants.filter(c => c.slug !== consultant.slug);
  const mailto = `mailto:${consultant.email}`;
  return <>
      <PageHero compact label={consultant.title} title={consultant.name} data-id="element-633" data-mp="thahhufp7qtf" />

      <section className="bg-porcelain py-16 lg:py-24" data-id="element-634" data-mp="xl8kwhc23nsa">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-635" data-mp="jke9rqxmj8px">
          <aside className="lg:col-span-4" data-id="element-636" data-mp="ojhetbdltexw">
            <div className="rounded-2xl border border-plum/10 bg-white p-6 lg:sticky lg:top-28" data-id="element-637" data-mp="okac7lqoas3d">
              <ConsultantAvatar consultant={consultant} data-id="element-638" data-mp="2dzqkkywrw9s7" />
              <h2 className="mt-6 text-2xl font-semibold text-aubergine" data-id="element-639" data-mp="2ixie1xd2g3yi">{consultant.name}</h2>
              <p className="mt-1 text-sm text-plum/65" data-id="element-640" data-mp="2hgcz0idba7fu">{consultant.title}</p>
              <a href={mailto} className="mt-4 block text-sm text-copper-deep underline decoration-copper/30 underline-offset-4 [overflow-wrap:anywhere] hover:decoration-copper" data-id="element-641" data-mp="2he9wiyuz2lu8">
                {consultant.email}
              </a>
              <a href={mailto} className={buttonClasses('primary', 'md', 'mt-6 w-full')} data-id="element-642" data-mp="22jc4y87ne0e7">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-643" data-mp="306ukq2zq82rp" />
                Email {consultant.firstName}
              </a>
              <Link to="/consultants" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-plum/70 hover:text-copper-deep" data-id="element-644" data-mp="2v7hst3u005i9">
                <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-645" data-mp="2qeybbvs6wq86" />
                All consultants
              </Link>
            </div>
          </aside>

          <div className="space-y-14 lg:col-span-7 lg:col-start-6" data-id="element-646" data-mp="3iydmy7lxfcso">
            <section aria-labelledby="intro-title" data-id="element-647" data-mp="3e6vn5mvn3i9a">
              <h2 id="intro-title" className="text-2xl font-semibold text-aubergine" data-id="element-648" data-mp="3e5aszyd8m1by">
                Professional introduction
              </h2>
              <p className="mt-4 max-w-prose text-lg leading-relaxed text-plum/75" data-id="element-649" data-mp="3qglendiwvqr1">{consultant.introduction}</p>
              {!consultant.biographyConfirmed && <p className="mt-4 flex gap-2 text-sm text-plum/60" data-id="element-650" data-mp="3vi0yx3d9lt8d">
                  <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-copper-deep" aria-hidden="true" data-id="element-651" data-mp="4afzsi1ejkae" />
                  Placeholder — an approved biography has not yet been provided.
                </p>}
            </section>

            <section aria-labelledby="expertise-title" className="border-t border-copper/30 pt-12" data-id="element-652" data-mp="d7yqa1xteqto">
              <h2 id="expertise-title" className="text-2xl font-semibold text-aubergine" data-id="element-653" data-mp="i3e44tvtomq3">
                Areas of expertise
              </h2>
              {consultant.expertise.length ? <ul className="mt-5 flex flex-wrap gap-2" data-id="element-654" data-mp="rxed56u86c43">
                  {consultant.expertise.map(e => <li key={e} className="rounded-full border border-plum/15 bg-white px-3.5 py-1.5 text-sm text-plum/80" data-id="element-655" data-mp="1tppb7fqadr1b">
                      {e}
                    </li>)}
                </ul> : <p className="mt-4 leading-relaxed text-plum/65" data-id="element-656" data-mp="23eh2rmrfq9bp">Confirmed areas of expertise will be listed here once provided.</p>}
            </section>

            <section aria-labelledby="approach-title" className="border-t border-copper/30 pt-12" data-id="element-657" data-mp="1aql5ogxyefee">
              <h2 id="approach-title" className="text-2xl font-semibold text-aubergine" data-id="element-658" data-mp="15s070lyco3s2">
                Search approach
              </h2>
              <ul className="mt-5 space-y-4" data-id="element-659" data-mp="1jsax3mru5ec6">
                {searchApproach.map(a => <li key={a} className="flex gap-3 leading-relaxed text-plum/75" data-id="element-660" data-mp="1fp4ucp3xm5ag">
                    <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-copper" aria-hidden="true" data-id="element-661" data-mp="2v5wws1vbmyby" />
                    {a}
                  </li>)}
              </ul>
            </section>

            <section aria-label="Get in touch" className="grid gap-5 border-t border-copper/30 pt-12 sm:grid-cols-2" data-id="element-662" data-mp="2yocfwv2wx64y">
              <div className="flex flex-col rounded-2xl bg-limestone p-6" data-id="element-663" data-mp="33ogswb4m3iqf">
                <h2 className="font-display text-lg font-semibold text-aubergine" data-id="element-664" data-mp="2or7wv44d5qsd">For candidates</h2>
                <p className="mt-2 text-sm leading-relaxed text-plum/75" data-id="element-665" data-mp="2topwu50ekvo8">
                  Introduce your background directly to {consultant.firstName} by email, or register your interest with the wider team.
                </p>
                <a href={mailto} className="mt-4 text-sm font-medium text-copper-deep [overflow-wrap:anywhere] hover:underline" data-id="element-666" data-mp="3mdn8hpa3zu80">
                  {consultant.email}
                </a>
                <ButtonLink to="/candidates#introduce" variant="ghost" arrow className="mt-auto pt-5" data-id="element-667" data-mp="3cn2e6e1gxbuw">
                  Introduce Yourself
                </ButtonLink>
              </div>
              <div className="on-dark flex flex-col rounded-2xl bg-aubergine p-6 text-porcelain" data-id="element-668" data-mp="90btyf3atnxq">
                <h2 className="font-display text-lg font-semibold" data-id="element-669" data-mp="iu7pvwdtiytm">For employers</h2>
                <p className="mt-2 text-sm leading-relaxed text-porcelain/75" data-id="element-670" data-mp="dvxsdf4mfm4u">
                  Planning a leadership appointment? Share your requirements and {consultant.firstName} or a colleague will be in touch.
                </p>
                <ButtonLink to="/employers#inquiry" size="sm" className="mt-auto self-start" data-id="element-671" data-mp="4uzpj05x5knu">
                  Submit Hiring Inquiry
                </ButtonLink>
              </div>
            </section>
          </div>
        </div>
      </section>

      <section className="border-t border-plum/10 bg-porcelain py-16" data-id="element-672" data-mp="3mbptfrmk5jo8">
        <div className="mx-auto max-w-7xl px-6 lg:px-10" data-id="element-673" data-mp="3m9wjo3gnejc0">
          <h2 className="font-display text-lg font-semibold text-aubergine" data-id="element-674" data-mp="3ibfhzzfzy11z">Other consultants</h2>
          <ul className="mt-6 grid gap-4 sm:grid-cols-3" data-id="element-675" data-mp="3da9nbw2s03pt">
            {others.map(c => <li key={c.slug} data-id="element-676" data-mp="38d9dda2t2gtf">
                <Link to={`/consultants/${c.slug}`} className="group flex items-center gap-4 rounded-xl border border-plum/10 bg-white p-4 transition-colors duration-150 hover:border-copper/50" data-id="element-677" data-mp="38czvyavtthzc">
                  <ConsultantAvatar consultant={c} size="sm" data-id="element-678" data-mp="1srfkah82lf7g" />
                  <span data-id="element-679" data-mp="1xs3h5jizcq9l">
                    <span className="block font-display font-semibold text-aubergine group-hover:text-copper-deep" data-id="element-680" data-mp="1xsv3e3qotbn7">{c.name}</span>
                    <span className="block text-xs text-plum/60" data-id="element-681" data-mp="21rm40kud3bf2">{c.title}</span>
                  </span>
                </Link>
              </li>)}
          </ul>
        </div>
      </section>
    </>;
}